/**
 * Knowledge Base Page
 * Manage help articles and documentation
 */
// @ts-nocheck

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  useColorModeValue,
  Tag,
  TagLabel,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  MdSearch,
  MdRefresh,
  MdMoreVert,
  MdVisibility,
  MdEdit,
  MdDelete,
  MdAdd,
  MdThumbUp,
  MdThumbDown,
  MdVisibilityOff,
  MdPublish,
  MdArchive,
  MdContentCopy,
  MdMenuBook,
  MdTranslate,
} from 'react-icons/md';
import { KnowledgeBaseArticle, KBStats, KBFilters, SupportTicket } from '../../types/support';
import { supportService } from '../services/supportService';

export function KnowledgeBasePage() {
  const navigate = useNavigate();
  const toast = useToast();
  
  const [articles, setArticles] = useState<KnowledgeBaseArticle[]>([]);
  const [stats, setStats] = useState<KBStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<KBFilters>({
    search: '',
    category: 'all',
    status: 'all',
    language: 'all',
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    loadArticles();
  }, [filters]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      const response = await supportService.getArticles(filters);
      setArticles(response.articles);
      // Stats would need a separate API call if needed
    } catch (error: any) {
      console.error('Error loading articles:', error);
      toast({
        title: 'Error loading articles',
        description: error.message || 'Please try again later',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      // Fallback to mock data on error
      setArticles(mockArticles);
      setStats(mockStats);
    } finally {
      setLoading(false);
    }
  };

  const publishArticle = async (articleId: string) => {
    try {
      // TODO: API call
      toast({
        title: 'Article published',
        status: 'success',
        duration: 3000,
      });
      loadArticles();
    } catch (error) {
      toast({
        title: 'Error publishing article',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const archiveArticle = async (articleId: string) => {
    try {
      // TODO: API call
      toast({
        title: 'Article archived',
        status: 'success',
        duration: 3000,
      });
      loadArticles();
    } catch (error) {
      toast({
        title: 'Error archiving article',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const getCategoryColor = (category: KnowledgeBaseArticle['category']) => {
    const colors: Record<KnowledgeBaseArticle['category'], string> = {
      getting_started: 'blue',
      payment_matching: 'green',
      integrations: 'purple',
      reports: 'orange',
      troubleshooting: 'red',
      billing: 'cyan',
      account_management: 'pink',
      other: 'gray',
    };
    return colors[category] || 'gray';
  };

  const getStatusColor = (status: KnowledgeBaseArticle['status']) => {
    switch (status) {
      case 'published': return 'green';
      case 'draft': return 'gray';
      case 'archived': return 'red';
      default: return 'gray';
    }
  };

  const getVisibilityColor = (visibility: KnowledgeBaseArticle['visibility']) => {
    switch (visibility) {
      case 'public': return 'green';
      case 'internal': return 'orange';
      case 'beta_users': return 'blue';
      default: return 'gray';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const calculateHelpfulRate = (article: KnowledgeBaseArticle) => {
    const total = article.helpfulVotes + article.notHelpfulVotes;
    if (total === 0) return 0;
    return (article.helpfulVotes / total) * 100;
  };

  return (
    <Box>
      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <VStack align="start" spacing={0}>
          <Heading size="lg">Knowledge Base</Heading>
          <Text color="gray.600" fontSize="sm">
            Manage help articles and documentation
          </Text>
        </VStack>
        <HStack>
          <Button
            leftIcon={<MdRefresh />}
            onClick={loadArticles}
            variant="outline"
            isLoading={loading}
          >
            Refresh
          </Button>
          <Button colorScheme="blue" leftIcon={<MdAdd />}>
            New Article
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      {stats && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4} mb={6}>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <HStack spacing={3}>
                  <Icon as={MdMenuBook} boxSize={8} color="blue.500" />
                  <Box>
                    <StatLabel>Total Articles</StatLabel>
                    <StatNumber>{stats.totalArticles}</StatNumber>
                    <StatHelpText>{stats.published} published</StatHelpText>
                  </Box>
                </HStack>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Total Views</StatLabel>
                <StatNumber>{stats.totalViews.toLocaleString()}</StatNumber>
                <StatHelpText>{stats.avgViewsPerArticle.toFixed(0)} avg/article</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Helpfulness</StatLabel>
                <StatNumber fontSize="lg">{stats.avgHelpfulRate.toFixed(1)}%</StatNumber>
                <StatHelpText color={stats.avgHelpfulRate >= 80 ? 'green.500' : 'orange.500'}>
                  User satisfaction
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <HStack spacing={3}>
                  <Icon as={MdTranslate} boxSize={8} color="purple.500" />
                  <Box>
                    <StatLabel>Languages</StatLabel>
                    <StatNumber>{stats.languageCount}</StatNumber>
                    <StatHelpText>EN, FR</StatHelpText>
                  </Box>
                </HStack>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Category Distribution */}
      {stats && (
        <Card bg={cardBg} borderColor={borderColor} mb={6}>
          <CardBody>
            <Heading size="sm" mb={4}>Articles by Category</Heading>
            <Wrap spacing={4}>
              {Object.entries(stats.byCategory).map(([category, count]) => (
                <WrapItem key={category}>
                  <HStack>
                    <Badge 
                      colorScheme={getCategoryColor(category as KnowledgeBaseArticle['category'])} 
                      fontSize="sm" 
                      px={3}
                      py={1}
                    >
                      {category.replace('_', ' ')}
                    </Badge>
                    <Text fontWeight="bold">{String(count)}</Text>
                  </HStack>
                </WrapItem>
              ))}
            </Wrap>
          </CardBody>
        </Card>
      )}

      {/* Filters */}
      <Card bg={cardBg} borderColor={borderColor} mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
            <InputGroup>
              <InputLeftElement>
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search articles..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </InputGroup>

            <Select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value as any })}
            >
              <option value="all">All Categories</option>
              <option value="getting_started">Getting Started</option>
              <option value="payment_matching">Payment Matching</option>
              <option value="integrations">Integrations</option>
              <option value="reports">Reports</option>
              <option value="troubleshooting">Troubleshooting</option>
              <option value="billing">Billing</option>
              <option value="account_management">Account Management</option>
              <option value="other">Other</option>
            </Select>

            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
            >
              <option value="all">All Status</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </Select>

            <Select
              value={filters.language}
              onChange={(e) => setFilters({ ...filters, language: e.target.value as any })}
            >
              <option value="all">All Languages</option>
              <option value="en">English</option>
              <option value="fr">Français</option>
            </Select>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Articles Table */}
      <Card bg={cardBg} borderColor={borderColor}>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md">Articles</Heading>
            <Text fontSize="sm" color="gray.600">
              {articles.length} articles
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Flex justify="center" align="center" h="200px">
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : articles.length === 0 ? (
            <Flex justify="center" align="center" h="200px" direction="column">
              <Icon as={MdMenuBook} boxSize={12} color="gray.400" mb={2} />
              <Text color="gray.600">No articles found</Text>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Title</Th>
                    <Th>Category</Th>
                    <Th>Language</Th>
                    <Th>Views</Th>
                    <Th>Helpfulness</Th>
                    <Th>Status</Th>
                    <Th>Visibility</Th>
                    <Th>Updated</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {articles.map((article) => {
                    const helpfulRate = calculateHelpfulRate(article);
                    
                    return (
                      <Tr
                        key={article.id}
                        _hover={{ bg: hoverBg }}
                        cursor="pointer"
                        onClick={() => navigate(`/admin/support/knowledge-base/${article.id}`)}
                      >
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="bold" noOfLines={1}>{article.title}</Text>
                            <Text fontSize="sm" color="gray.600" noOfLines={1}>
                              {article.summary}
                            </Text>
                            <Wrap spacing={1}>
                              {article.tags.slice(0, 3).map((tag) => (
                                <Tag key={tag} size="sm" colorScheme="gray">
                                  <TagLabel>{tag}</TagLabel>
                                </Tag>
                              ))}
                            </Wrap>
                          </VStack>
                        </Td>
                        <Td>
                          <Badge colorScheme={getCategoryColor(article.category)}>
                            {article.category.replace('_', ' ')}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme="purple">
                            {article.language.toUpperCase()}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontWeight="bold">{article.viewCount.toLocaleString()}</Text>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <HStack>
                              <Icon as={MdThumbUp} color="green.500" boxSize={4} />
                              <Text fontSize="sm">{article.helpfulVotes}</Text>
                              <Icon as={MdThumbDown} color="red.500" boxSize={4} />
                              <Text fontSize="sm">{article.notHelpfulVotes}</Text>
                            </HStack>
                            <Text 
                              fontSize="xs" 
                              color={helpfulRate >= 80 ? 'green.600' : 'orange.600'}
                              fontWeight="bold"
                            >
                              {helpfulRate.toFixed(0)}% helpful
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(article.status)}>
                            {article.status}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={getVisibilityColor(article.visibility)}>
                            {article.visibility.replace('_', ' ')}
                          </Badge>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm">{formatDate(article.updatedAt)}</Text>
                            <Text fontSize="xs" color="gray.600">
                              by {article.authorName}
                            </Text>
                          </VStack>
                        </Td>
                        <Td onClick={(e) => e.stopPropagation()}>
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<MdMoreVert />}
                              variant="ghost"
                              size="sm"
                            />
                            <MenuList>
                              <MenuItem
                                icon={<MdVisibility />}
                                onClick={() => navigate(`/admin/support/knowledge-base/${article.id}`)}
                              >
                                View Article
                              </MenuItem>
                              <MenuItem icon={<MdEdit />}>
                                Edit
                              </MenuItem>
                              {article.status === 'draft' && (
                                <MenuItem
                                  icon={<MdPublish />}
                                  onClick={() => publishArticle(article.id)}
                                >
                                  Publish
                                </MenuItem>
                              )}
                              <MenuItem icon={<MdContentCopy />}>
                                Duplicate
                              </MenuItem>
                              <MenuItem
                                icon={<MdArchive />}
                                onClick={() => archiveArticle(article.id)}
                              >
                                Archive
                              </MenuItem>
                              <MenuItem icon={<MdDelete />} color="red.500">
                                Delete
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
}

// Mock Data
const mockArticles: KnowledgeBaseArticle[] = [
  {
    id: '1',
    title: 'Getting Started with Payment Matching',
    slug: 'getting-started-payment-matching',
    summary: 'Learn how to set up and use the automated payment matching feature',
    content: '# Getting Started\n\nThis guide will help you...',
    category: 'payment_matching',
    tags: ['payments', 'matching', 'automation', 'getting-started'],
    relatedArticles: ['2', '3'],
    relatedTicketCategories: ['payment_processing'],
    status: 'published',
    visibility: 'public',
    searchKeywords: ['payment', 'matching', 'automatic', 'bank'],
    viewCount: 1250,
    helpfulVotes: 98,
    notHelpfulVotes: 12,
    avgTimeRead: 180,
    authorId: 'admin1',
    authorName: 'Admin User',
    createdAt: new Date('2025-09-01'),
    updatedAt: new Date('2025-11-15'),
    publishedAt: new Date('2025-09-05'),
    lastReviewedAt: new Date('2025-11-01'),
    language: 'en',
  },
  {
    id: '2',
    title: 'Troubleshooting Gmail Integration Issues',
    slug: 'troubleshooting-gmail-integration',
    summary: 'Common solutions for Gmail sync problems',
    content: '# Troubleshooting Gmail\n\nIf you\'re experiencing...',
    category: 'troubleshooting',
    tags: ['gmail', 'integration', 'oauth', 'sync'],
    relatedArticles: ['1'],
    relatedTicketCategories: ['integrations'],
    status: 'published',
    visibility: 'public',
    searchKeywords: ['gmail', 'email', 'sync', 'oauth', 'not working'],
    viewCount: 890,
    helpfulVotes: 75,
    notHelpfulVotes: 8,
    avgTimeRead: 240,
    authorId: 'admin1',
    authorName: 'Admin User',
    createdAt: new Date('2025-10-01'),
    updatedAt: new Date('2025-11-20'),
    publishedAt: new Date('2025-10-05'),
    lastReviewedAt: new Date('2025-11-15'),
    language: 'en',
  },
];

const mockStats: KBStats = {
  totalArticles: 2,
  published: 2,
  draft: 0,
  archived: 0,
  totalViews: 2140,
  avgViewsPerArticle: 1070,
  avgHelpfulRate: 88.5,
  languageCount: 1,
  byCategory: {
    getting_started: 0,
    payment_matching: 1,
    integrations: 0,
    reports: 0,
    troubleshooting: 1,
    billing: 0,
    account_management: 0,
    other: 0,
  },
};
